import { UnitStateV2 } from '@eppendorf/vnls-device-service-utils';
import { Badge, HasClassName } from '@eppendorf/vnls-react-components';
import { ReactElement } from 'react';

import { getLocalizedDate } from '$shared/utils/date.utils';

import { getActivityStatusText } from '$components/device/activity-status/activity-status.utils';

export interface ActivityStatusProps extends HasClassName {
  activityStatus: UnitStateV2 | null;
  bordered?: boolean;
  offlineSince?: number;
  size?: 'small' | 'medium';
}

function getStatusColor(
  status: UnitStateV2,
): 'green' | 'orange' | 'blue' | 'red' | undefined {
  switch (status) {
    case UnitStateV2.Paused:
    case UnitStateV2.Hold:
    case UnitStateV2.Preparing:
      return 'orange';

    case UnitStateV2.AutomaticallyRunning:
    case UnitStateV2.ManuallyRunning:
    case UnitStateV2.Running:
    case UnitStateV2.Done:
    case UnitStateV2.Online:
      return 'green';

    case UnitStateV2.Idle:
      return 'blue';

    case UnitStateV2.Unreachable:
    case UnitStateV2.Error:
      return 'red';

    default:
      return undefined;
  }
}

export function ActivityStatus({
  activityStatus,
  className,
  bordered = false,
  offlineSince,
  size,
}: ActivityStatusProps): ReactElement | null {
  if (!activityStatus) return null;

  if (activityStatus === UnitStateV2.Offline) {
    const text = getActivityStatusText(UnitStateV2.Offline);
    const dateText = offlineSince ? ` since ${getLocalizedDate(offlineSince)}` : '';
    return (
      <Badge bordered={bordered} size={size}>
        {text + dateText}
      </Badge>
    );
  }

  const color = activityStatus ? getStatusColor(activityStatus) : undefined;
  const text = getActivityStatusText(activityStatus);

  const badge = (
    <Badge bordered={bordered} color={color} size={size}>
      {text}
    </Badge>
  );

  return <div className={className}>{badge}</div>;
}
